import { head, get, isEmpty, compact, defaultTo } from 'lodash-es';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { OG_TYPE_PRODUCT } from 'site-modules/shared/constants/og-types';
import { getCarVinSchema } from 'client/seo/schemas/car';
import { getVehicleVinSchema } from 'client/seo/schemas/vehicle';
import { getProductSchema } from 'client/seo/schemas/product';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { getModelState } from 'client/data/luckdragon/model';
import { UrlModel, URL_CONTEXT_PATH } from 'client/data/models/url';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getSeoImage } from 'site-modules/shared/utils/inventory-utils/get-seo-image';
import { getNonAmpUrl } from 'site-modules/shared/utils/url-utils';
import { processAmpCanonicals, generateCanonical, generateRelLink } from 'site-modules/shared/pages/seo';
import { InventoryModel } from 'client/data/models/inventory';
import { VisitorModel } from 'client/data/models/visitor';
import { getPrimaryImageOfPage } from 'client/site-modules/shared/utils/inventory/srp-images';
import { getWebpageSchema } from 'client/seo/schemas/webpage';
import {
  buildSearchFilterSEOObject,
  getPaginationUrls,
  getRobots,
  generateSEOTitle,
  generateSEODescription,
  generateSEOKeywords,
} from 'site-modules/shared/utils/inventory/srp-seo-utils';
import { getSrpBreadcrumbs } from 'site-modules/shared/utils/inventory/srp-breadcrumbs';
import { getUrlPattern } from 'site-modules/shared/utils/inventory/srp-utils';
import { SEOModel } from 'client/data/models/seo';

const IMAGE_DATA_PATH = 'vehicleInfo.photo.defaultPhoto.largePhotoCount';
export const SEOT4148PATH = '/used-toyota-land-cruiser-ontario-ca/';
function transformConsumerReviews(consumerReviews) {
  return {
    reviews: get(consumerReviews, 'reviews', []).map(review => ({
      ...review,
      author: {
        authorName: review.userName,
      },
      created: review.createDate,
    })),
    ratingAggregation: {
      averageStars: get(consumerReviews, 'averageConsumerRatings', 0),
    },
    totalReviews: get(consumerReviews, 'totalReviewCount', 0),
  };
}

/**
 * Get seo object
 * @param state {object}
 * @param props {object}
 * @returns {object}
 */
export function getSrpSeoObject(state, props) {
  const modelState = getModelState(state);
  const disableInventoryMLPhotoSort = !!get(state, 'featureFlags["disable-inventory-ml-photo-sort"]');
  const params = get(props, 'match.params', {});
  const pathName = props.location.pathname;
  const urlContext = modelState.get(URL_CONTEXT_PATH, UrlModel) || {};
  const {
    isSrpOnUsedCore = false,
    vehicle = {},
    redirectUrl = '',
    location: { city = '', stateName = '' } = {},
  } = urlContext;
  const visitorLocation = modelState.get('location', VisitorModel);
  const searchResultsFilter = modelState.get('searchResultsFilter', InventoryModel);
  const pageData = modelState.get('searchResults', InventoryModel);
  const totalNumber = get(pageData, 'inventories.totalNumber', 0);
  const seoInfo = get(pageData, 'seoInfo', {});
  const results = get(pageData, 'inventories.results', []);
  const attributes = get(pageData, 'attributes', {});
  const reviewsData = get(seoInfo, 'consumerReviews', {});
  const urlPattern = getUrlPattern(urlContext);
  const baseInventory = !isEmpty(results) ? head(results) : {};
  const stockPhotos = [];
  const image = getSeoImage(baseInventory, stockPhotos, IMAGE_DATA_PATH);
  const pageNumber = get(pageData, 'inventories.pageNumber', 1);
  const pagesCount = get(pageData, 'inventories.totalPages');
  const seoData = buildSearchFilterSEOObject({
    searchResultsFilter,
    urlContext,
    inventories: results,
    attributes,
    inventoryCount: totalNumber,
    pageNumber,
  });
  const { make, model, year } = seoData;
  const isLeaseSrp = seoData.isLease;
  const paginationUrls = isSrpOnUsedCore
    ? getPaginationUrls({
        pageNumber,
        pagesCount,
        pathName,
      })
    : getPaginationUrls({
        pageNumber,
        pagesCount,
        selectedFacets: searchResultsFilter,
        urlContext,
        pathName,
      });
  const makeSlug = get(vehicle, 'make.slug');
  const redirectUrlPathnameObj = redirectUrl ? { pathname: redirectUrl } : null;
  const canonical = processAmpCanonicals(generateCanonical(redirectUrlPathnameObj || props.location));

  const seoExperiments = defaultTo(modelState.get('seoExperiments', SEOModel), []);
  const isSeot4654Enabled =
    seoExperiments.includes('meta_description_removal_MMY_srp') &&
    !!get(state, 'featureFlags["enable-seot-4654-remove-mmy-srp-meta-description"]');

  const title = generateSEOTitle({
    seoData,
    visitorLocation,
    isMeta: true,
  });

  const description = isSeot4654Enabled ? null : generateSEODescription(totalNumber, seoInfo, seoData, results);

  const isSeot4290 =
    !!get(state, 'featureFlags["enable-seot-4290-car-type-schema-2022"]') &&
    year === '2022' &&
    urlPattern === 'make/model/year';
  const seoObj = {
    title,
    description,
    keywords: generateSEOKeywords(urlContext, seoData),
    canonical,
    image,
    type: OG_TYPE_PRODUCT,
    jsonld: results.map(result =>
      isSrpOnUsedCore
        ? getVehicleVinSchema({ vehicle: result, disableInventoryMLPhotoSort, isSeot4290, city, stateName, urlPattern })
        : getCarVinSchema({
            vinData: result,
            disableInventoryMLPhotoSort,
            city,
            stateName,
            urlPattern,
          })
    ),
    robots: getRobots(searchResultsFilter, urlContext, seoInfo),
    prev: generateRelLink(paginationUrls.prev),
    next: generateRelLink(paginationUrls.next),
  };

  const inventoryType = get(urlContext, 'attributes.inventory.inventoryType');
  const isNewType = inventoryType && isNew(inventoryType);
  const hasWebpageSchema = !isNewType && makeSlug && results.length;
  const webpageSchema = hasWebpageSchema
    ? {
        ...getWebpageSchema(title),
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: getPrimaryImageOfPage(results),
        },
      }
    : {};
  if (hasWebpageSchema) {
    seoObj.jsonld.push(webpageSchema);
  }

  const faqApiData = get(seoInfo, 'faqWidget');
  const hasFaqApiData = !!faqApiData && faqApiData.items.length > 0;
  if (hasFaqApiData) {
    const faqContent = compact(get(faqApiData, 'items'));
    seoObj.jsonld.push(
      getFAQSchema({
        entries: faqContent,
        generateLearnMoreLinks: true,
        withTracking: !isNewType,
        learnMoreLinkText: get(faqApiData, 'learnMoreText'),
        pathname: pathName,
      })
    );
  }

  if (isLeaseSrp) {
    const srpNarrativeText = get(seoInfo, 'narrativeWidget.items');
    const learnMoreLinkText = get(seoInfo, 'narrativeWidget.learnMoreText');

    if (srpNarrativeText) {
      seoObj.jsonld.push(
        getFAQSchema({
          entries: srpNarrativeText,
          questionLabel: 'heading',
          answerLabel: 'content',
          generateLearnMoreLinks: true,
          withTracking: true,
          learnMoreLinkText,
          pathname: pathName,
        })
      );
    }
  }

  seoObj.jsonld.push(
    getBreadcrumbSchema(
      getSrpBreadcrumbs({
        seoData,
        vehicle: attributes.vehicle,
        routeParams: params,
        pathName: getNonAmpUrl(pathName),
      })
    )
  );

  if (isSrpOnUsedCore) {
    seoObj.keywords = null;
  }

  seoObj.jsonld = compact(seoObj.jsonld);

  const vehicleType = get(attributes, 'type');
  const isSeot4148 = pathName === SEOT4148PATH && hasWebpageSchema;
  const productSchema = getProductSchema({
    searchResults: pageData,
    image,
    consumerReviews: transformConsumerReviews(reviewsData),
    description,
    vehicleType,
    make,
    model,
    year,
    isSeot4148,
  });

  if (!productSchema) {
    return seoObj;
  }

  if (isSeot4148) {
    webpageSchema.mainEntity = [];
  }
  const productSchemaLocation = isSeot4148 ? webpageSchema.mainEntity : seoObj.jsonld;
  productSchemaLocation.push(productSchema);

  return seoObj;
}
